<template>
  <div>
    <h1 class="mb-8 font-bold text-3xl flex items-center">
      <span>Settings</span>
      <span v-if="dirty" class="ml-4 text-sm text-red-600">Unsaved changes</span>
    </h1>

    <h2 class="mb-8 font-bold text-xl flex items-center">
      <span>Define how SpethBot behaves in Twitch Chat.</span>
    </h2>

    <div class="bg-white rounded-md shadow p-6 max-w-2xl">
      <form @submit.prevent="save">
        <div class="grid grid-cols-1 gap-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <div>
                <div class="font-medium">Bot Controls</div>
                <div class="text-xs text-gray-500">Mute or enable all bot features</div>
              </div>
            </div>
            <div class="flex items-center">
              <button type="button" class="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded mr-2" @click="muteAll">Mute Bot</button>
              <button type="button" class="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded" @click="enableAll">Enable All</button>
            </div>
          </div>

          <div class="pt-2 border-t"></div>

          <div>
              <label class="flex items-center justify-between" :class="{ 'opacity-50': form.mute_bot && !form.announcements }">
                <div>
                  <div class="font-medium">Announcements</div>
                  <div class="text-xs text-gray-500">Messages posted when using !linkson.</div>
                </div>
                <input type="checkbox" v-model="form.announcements" />
              </label>

              <label class="flex items-center justify-between" :class="{ 'opacity-50': form.mute_bot && !form.announcements }">
                <div>
                  <div class="font-medium">Announcement delay (minutes)</div>
                  <div class="text-xs text-gray-500">Time between announcement messages.</div>
                </div>
                <input type="number" v-model.number="form.announcement_delay" class="form-input w-24 text-right" />
              </label>
          </div>

          <label class="flex items-center justify-between" :class="{ 'opacity-50': form.mute_bot && !form.subscription_messages }">
            <div>
              <div class="font-medium">Subscription Messages</div>
              <div class="text-xs text-gray-500">Send a message when users subscribe or gift a subscription.</div>
            </div>
            <input type="checkbox" v-model="form.subscription_messages" />
          </label>
          
          <label class="flex items-center justify-between" :class="{ 'opacity-50': form.mute_bot && !form.commands }">
            <div>
              <div class="font-medium">Commands</div>
              <div class="text-xs text-gray-500">Does not effect if individual commands are enabled or disabled, when disabled SpethBot will not reply to ANY commands. </div>
            </div>
            <input type="checkbox" v-model="form.commands" />
          </label>

          <label class="flex items-center justify-between" :class="{ 'opacity-50': form.mute_bot && !form.quotes }">
            <div>
              <div class="font-medium">Quotes</div>
              <div class="text-xs text-gray-500">When disabled, SpethBot will not send quotes.</div>
            </div>
            <input type="checkbox" v-model="form.quotes" />
          </label>

          <label v-if="$posthog.isFeatureEnabled('reactions-tab')" class="flex items-center justify-between" :class="{ 'opacity-50': form.mute_bot && !form.reactions }">
            <div>
              <div class="font-medium">Reactions</div>
              <div class="text-xs text-gray-500">If SpethBot should react to defined messages or not.</div>
            </div>
            <input type="checkbox" v-model="form.reactions" />
          </label>

          <label v-if="$posthog.isFeatureEnabled('automod-tab')" class="flex items-center justify-between" :class="{ 'opacity-50': form.mute_bot && !form.auto_moderation }">
            <div>
              <div class="font-medium">Auto Moderation</div>
              <div class="text-xs text-gray-500">Define if SpethBot should automoderate or not.</div>
            </div>
            <input type="checkbox" v-model="form.auto_moderation" />
          </label>

          <div class="pt-4">
            <button class="btn-indigo" type="submit">Save</button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Layout from '@/Shared/Layout'
import Icon from '@/Shared/Icon'

export default {
  metaInfo: { title: 'Settings' },
  layout: Layout,
  components: { Icon },
  props: {
    settings: Object,
  },
  data() {
    return {
      initial: Object.assign({}, this.settings || {}),
      form: Object.assign({
        mute_bot: false,
        commands: false,
        quotes: false,
        reactions: false,
        announcements: false,
        auto_moderation: false,
        subscription_messages: false,
        announcement_delay: 0,
      }, this.settings || {}),
    }
  },
  computed: {
    dirty() {
      return JSON.stringify(this.form) !== JSON.stringify(this.initial)
    },
  },
  watch: {
    settings(newSettings) {
      // When props update after a successful save, sync initial to prevent dirty flag from re-triggering
      this.initial = Object.assign({}, newSettings || {})
    },
  },
  methods: {
    save() {
      // Check if all bool properties are disabled (complete mute)
      const allDisabled = !this.form.commands && !this.form.quotes && !this.form.reactions && !this.form.announcements && !this.form.auto_moderation && !this.form.subscription_messages
      
      if (allDisabled) {
        if (!confirm('Mute bot — are you sure? This will disable all bot features.')) {
          return
        }
      }
      
      this.$inertia.put(this.route('settings.update'), this.form).then(() => {
        this.initial = Object.assign({}, this.form)
        this.dirty = false
      })
    },
    muteAll() {
      this.form.mute_bot = true
      ;['commands','quotes','reactions','announcements','auto_moderation','subscription_messages'].forEach(k => {
        this.form[k] = false
      })
    },
    enableAll() {
      this.form.mute_bot = false
      ;['commands','quotes','reactions','announcements','auto_moderation','subscription_messages'].forEach(k => {
        this.form[k] = true
      })
    },
  },
}
</script>
